/* eslint-disable max-len */
import { useState, useCallback, useEffect } from 'react';
import _ from 'underscore';
import { isListValid, isRequiredValid } from './helper';
import {
  FileListChangeHandler, IFileEntry, IFileInput, ValidatorFn,
} from './types';

const calculateEachFileValidationResult = (
  fileList: IFileInput[],
  fileValidation: ValidatorFn
): IFileEntry[] => _.map(fileList, ({
  file,
  ...rest
}) => ({
  ...rest,
  ...fileValidation(file),
  file,
})) as IFileEntry[];

interface UseThrowOutParams {
  fileValidation: ValidatorFn;
  onFileChange: FileListChangeHandler;
  required: boolean;
  fileList: IFileInput[];
}

export const useThrowOut = ({
  fileValidation,
  onFileChange,
  required,
  fileList
}: UseThrowOutParams) => {
  const [edited, setEdited] = useState(false);
  const [validatedFileList, setValidatedFileList] = useState(() => calculateEachFileValidationResult(fileList, fileValidation));
  const [displayEmptyFile, setDisplayEmptyFile] = useState(() => _.isEmpty(fileList));

  const setFileList = useCallback((newFileList: IFileEntry[], shouldValidateFile = false) => {
    setValidatedFileList(shouldValidateFile
      ? calculateEachFileValidationResult(newFileList, fileValidation)
      : newFileList);
  }, [fileValidation]);

  useEffect(() => {
    const isValid = isListValid(validatedFileList) && isRequiredValid({ list: validatedFileList, required });
    onFileChange({
      fileList: _.map(validatedFileList, file => _.omit(file, 'errorMessages')),
      isValid,
    });
  }, [validatedFileList, onFileChange, required]);

  return {
    validatedFileList,
    edited,
    displayEmptyFile,
    setEdited,
    setDisplayEmptyFile,
    setFileList,
  };
};
